import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import {
  PageContent,
  Spinner,
  CisContainer,
  Button,
  Row,
  ButtonIcon,
  Modal,
  Table,
} from 'components';
import PropTypes from 'prop-types';
import {
  loadIpmaCisCreator,
  setIpmaCisPageCreator,
  setIpmaCisPerPageCreator,
  setIpmaCisSortCreator,
  setIpmaCisFiltersCreator,
  removeIpmaCisFiltersCreator,
  setIpmaCisProfileCreator,
} from 'store/actionsCreators';
import { isLoading, globalPropTypes, checkPermissions } from 'helpers';
import {
  loadGlobalCisInvoicesAttributesCreator,
  loadGlobalCisOrganizationsAttributesCreator,
  removeIpmaCisCreator,
} from '../../../../store/actionsCreators';
import {
  organizationIndustryOptions,
  organizationSizeOptions,
  organizationStatusOptions,
  organizationTypeOptions,
} from '../../../../shared/Cis/CisOptions';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import CisTable from 'components/Table/CisTable';
import CisPagination from 'components/Table/CisPagination';
import { formatDate } from 'helpers/date';

const Organizations = ({
  loading,
  list,
  loadList,
  setPage,
  setPerPage,
  setSort,
  setFilters,
  removeFilters,
  setProfile,
  removeOrganization,
  countries,
  certificationBodies,
  loadOrganizationsAttributes,
  userPermissions,
}) => {
  const { filters, items, page, pages, perPage, sort, totalItems } = list;
  const { t } = useTranslation();
  const history = useHistory();
  const [portalReady, setPortalReady] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const manageCisPermission = checkPermissions({ roles: ['ipma_cis'] }, userPermissions || []);

  useEffect(() => {
    if (document.getElementById('ipma-cis-buttons-portal')) {
      setPortalReady(true);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    loadList();
  }, [perPage, sort]);

  useEffect(() => {
    loadOrganizationsAttributes();
  }, []);

  const getSortDirection = (key) => {
    if (!sort) return null;
    const [sortKey, sortDir] = sort.split('.');
    return sortKey === key ? sortDir : null;
  };

  const handleColumnSort = (key, direction) => {
    if (direction) {
      setSort(`${key}.${direction}`);
    } else {
      setSort(null);
    }
  };

  const handleColumnFilterChange = (key, value) => {
    setFilters({ name: key, value });
    setPage(1);
    loadList();
  };

  return (
    <PageContent>
      <CisContainer>
        <CisTable>
          <CisTable.Head>
            <CisTable.Row>
              <CisTable.StickyHeading>
                <TableHeaderCell label={t('global.id')} />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.country || ''}
                  onFilter={(val) => handleColumnFilterChange('country', val)}
                  filterOptions={countries}
                  filterPlaceholder={t('page.user.cis.organizations.countryPlaceholder')}
                  label={t('page.user.cis.organizations.country')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.name || ''}
                  onFilter={(val) => handleColumnFilterChange('name', val)}
                  filterPlaceholder={t('page.user.cis.organizations.namePlaceholder')}
                  label={t('page.user.cis.organizations.name')}
                  sort
                  sortDirection={getSortDirection('name')}
                  onSort={(dir) => handleColumnSort('name', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.address || ''}
                  onFilter={(val) => handleColumnFilterChange('address', val)}
                  filterPlaceholder={t('page.user.cis.organizations.addressPlaceholder')}
                  label={t('page.user.cis.organizations.address')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.status || ''}
                  onFilter={(val) => handleColumnFilterChange('status', val)}
                  filterOptions={organizationStatusOptions}
                  filterPlaceholder={t('page.user.cis.organizations.statusPlaceholder')}
                  label={t('page.user.cis.organizations.status')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="datepicker-range"
                  filterValueFrom={filters.agreementSignedDateFrom || ''}
                  filterValueTo={filters.agreementSignedDateTo || ''}
                  onFilterFrom={(val) => handleColumnFilterChange('agreementSignedDateFrom', val)}
                  onFilterTo={(val) => handleColumnFilterChange('agreementSignedDateTo', val)}
                  filterPlaceholder={t(
                    'page.user.cis.organizations.agreementSignedDatePlaceholder',
                  )}
                  label={t('page.user.cis.organizations.agreementSignedDate')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.contactPerson || ''}
                  onFilter={(val) => handleColumnFilterChange('contactPerson', val)}
                  filterPlaceholder={t('page.user.cis.organizations.contactPersonPlaceholder')}
                  label={t('page.user.cis.organizations.contactPerson')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  label={t('page.user.cis.organizations.kamUser')}
                  filter
                  filterType="input"
                  filterValue={filters.kamUser || ''}
                  onFilter={(val) => handleColumnFilterChange('kamUser', val)}
                  filterPlaceholder={t('page.user.cis.organizations.kamUserPlaceholder')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.certificationBody || ''}
                  onFilter={(val) => handleColumnFilterChange('certificationBody', val)}
                  filterOptions={certificationBodies}
                  filterPlaceholder={t('page.user.cis.organizations.certificationBodyPlaceholder')}
                  label={t('page.user.cis.organizations.certificationBody')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.industries || ''}
                  onFilter={(val) => handleColumnFilterChange('industries', val)}
                  filterOptions={organizationIndustryOptions}
                  filterPlaceholder={t('page.user.cis.organizations.industriesPlaceholder')}
                  label={t('page.user.cis.organizations.industries')}
                />
              </CisTable.StickyHeading>
              {manageCisPermission && (
                <CisTable.StickyActionHeading style={{ minWidth: '10rem' }}>
                  <TableHeaderCell
                    label={t('page.user.cis.organizations.actions')}
                    labelAlign="center"
                    isAction={true}
                  />
                </CisTable.StickyActionHeading>
              )}
            </CisTable.Row>
          </CisTable.Head>
          <CisTable.Body>
            {loading ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={11}>
                  <Spinner />
                </CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && !items.length ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={11}>{t('global.noResultsFound')}</CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && items.length
              ? items.map((item) => (
                  <CisTable.Row key={item.id}>
                    <CisTable.Cell>
                      <Table.Label>{t('global.id')}:</Table.Label>
                      {item.id}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.organizations.country')}:</Table.Label>
                      {item.country?.name}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.organizations.name')}:</Table.Label>
                      {item.name}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.organizations.address')}:</Table.Label>
                      {`${item.address?.line1}, ${item.address?.city}, ${item.address?.country?.name}`}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.organizations.status')}:</Table.Label>
                      {organizationStatusOptions.find((x) => x.value === item.status)?.label}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>
                        {t('page.user.cis.organizations.agreementSignedDate')}:
                      </Table.Label>
                      {item.agreementSignedDate &&
                        formatDate(item.agreementSignedDate)}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.organizations.contactPerson')}:</Table.Label>
                      {item.contactPerson}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.organizations.kamUser')}:</Table.Label>
                      {item.kamUser && `${item.kamUser.firstName} ${item.kamUser.lastName}`}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>
                        {t('page.user.cis.organizations.certificationBody')}:
                      </Table.Label>
                      {item.certificationBody?.name}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.organizations.industries')}:</Table.Label>
                      {item.industries}
                    </CisTable.Cell>
                    {manageCisPermission && (
                      <CisTable.StickyActionCell style={{ minWidth: '9.6rem' }}>
                        <Table.Label>{t('page.user.cis.organizations.actions')}:</Table.Label>
                        <Row style={{ gap: '3px', justifyContent: 'center' }}>
                          <ButtonIcon
                            icon="edit"
                            secondary
                            as={Button}
                            onClick={() => {
                              setProfile(item);
                              history.push('/ipma/cis/organizations/edit');
                            }}
                          />
                          <ButtonIcon
                            icon="trash"
                            onClick={() => {
                              setOpenConfirmModal(true);
                              setSelectedItem(item);
                            }}
                          />
                        </Row>
                      </CisTable.StickyActionCell>
                    )}
                  </CisTable.Row>
                ))
              : null}
          </CisTable.Body>
        </CisTable>
      </CisContainer>
      <CisPagination
        styled
        currentPage={page}
        totalPages={pages}
        totalItems={totalItems}
        perPage={perPage}
        onPageChange={(payload) => {
          setPage(payload);
          loadList();
        }}
        onPerPageChange={({ value }) => setPerPage(value)}
      />
      <Modal
        heading={t('page.user.cis.organizations.confirmDelete')}
        isOpen={openConfirmModal && selectedItem?.id}
        confirm={() => {
          removeOrganization(selectedItem.id);
          setOpenConfirmModal(false);
        }}
        cancel={() => setOpenConfirmModal(false)}
        onRequestClose={() => setOpenConfirmModal(false)}
      />
      {portalReady &&
        manageCisPermission &&
        ReactDOM.createPortal(
          <Button style={{ width: '30%' }} secondary as={Link} to="/ipma/cis/organizations/create">
            {t('page.user.cis.organizations.new')}
          </Button>,
          document.getElementById('ipma-cis-buttons-portal'),
        )}
    </PageContent>
  );
};

Organizations.propTypes = {
  loading: PropTypes.bool,
  list: globalPropTypes.ListPropsTypes.isRequired,
  countries: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  certificationBodies: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  removeFilters: PropTypes.func.isRequired,
  setProfile: PropTypes.func.isRequired,
  removeOrganization: PropTypes.func.isRequired,
};

Organizations.defaultProps = {
  loading: false,
};

const mapStateToProps = ({ loading, ipma, globals, user }) => ({
  loading: isLoading(loading, 'LOAD_IPMA_CIS'),
  list: ipma.cis.organizations,
  countries: globals.countries,
  certificationBodies: globals.cis.certificationBodies,
  userPermissions: user.userPermissions,
});

const mapDispatchToProps = (dispatch) => ({
  loadList: () => dispatch(loadIpmaCisCreator('organizations')),
  setPage: (payload) => dispatch(setIpmaCisPageCreator('organizations', payload)),
  setPerPage: (payload) => dispatch(setIpmaCisPerPageCreator('organizations', payload)),
  setSort: (payload) => dispatch(setIpmaCisSortCreator('organizations', payload)),
  setFilters: (payload) => dispatch(setIpmaCisFiltersCreator('organizations', payload)),
  removeFilters: () => dispatch(removeIpmaCisFiltersCreator('organizations')),
  setProfile: (payload) => dispatch(setIpmaCisProfileCreator('organizations', payload)),
  removeOrganization: (payload) => dispatch(removeIpmaCisCreator('organizations', payload)),
  loadOrganizationsAttributes: () => dispatch(loadGlobalCisOrganizationsAttributesCreator()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Organizations);
